import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestoreIcon from "@mui/icons-material/Restore";
import { BADGE_COLORS, getBadgeStyles } from "../../../components/ui/badgeColors";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import DateUtils from "../../../utils/DateUtils";

/**
 * Definiciones de columnas para DataGrid de usuarios
 */

// CLAUDE.md, "Estados Especiales": "Sin asignar" en gris (#6c757d), nunca en
// rojo/negro. Reutilizado por la columna "rol" cuando el usuario no tiene rol
// asignado (rol: null es una forma válida devuelta por el backend/mapUsuario)
const SIN_ASIGNAR_LABEL = "Sin asignar";

const ESTADO_ACTIVO = "Activo";
const ESTADO_DE_BAJA = "De baja";

/**
 * Genera las columnas del DataGrid de usuarios
 * @param {Object} callbacks - Callbacks de acciones de fila y estado de operación en curso
 * @param {Function} callbacks.onEditar - Callback al pulsar editar, recibe la fila completa
 * @param {Function} callbacks.onDarBaja - Callback al pulsar "Dar de baja", recibe la fila completa
 * @param {Function} callbacks.onRestaurar - Callback al pulsar "Restaurar", recibe la fila completa
 * @param {number|null} [callbacks.idOperacionEnCurso] - Id del usuario cuya fila tiene una
 *   operación (baja/restaurar/editar) en curso; sus acciones se deshabilitan para evitar doble
 *   clic. null cuando ninguna fila está ocupada
 * @returns {Array} Definiciones de columnas para el DataGrid
 */
export const getUsuariosColumns = ({
    onEditar,
    onDarBaja,
    onRestaurar,
    idOperacionEnCurso = null,
} = {}) => [
    {
        field: "username",
        headerName: "Usuario",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
            <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, fontWeight: 600, color: "#495057" }}>
                {params.value}
            </Typography>
        ),
    },
    {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => (
            <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: "#495057" }}>{params.value}</Typography>
        ),
    },
    {
        field: "rol",
        headerName: "Rol",
        flex: 1,
        minWidth: 130,
        // rol puede ser null (usuario sin rol asignado, forma válida del backend/mapUsuario);
        // el valueGetter deja el dato listo para ordenar/filtrar por nombre sin exponer el objeto
        valueGetter: (params) => params.row.rol?.nombre ?? null,
        renderCell: (params) => (
            <Typography
                sx={{
                    fontSize: DATA_GRID_FONT_SIZE.CELDA,
                    color: params.value ? "#495057" : "#6c757d",
                }}
            >
                {params.value || SIN_ASIGNAR_LABEL}
            </Typography>
        ),
    },
    {
        field: "estado",
        headerName: "Estado",
        flex: 1,
        minWidth: 140,
        sortable: true,
        // fechaBaja es la fuente de verdad del estado (null -> Activo, con fecha -> De baja);
        // el valueGetter expone el texto para que ordenar/filtrar la columna tenga sentido
        valueGetter: (params) => (params.row.fechaBaja ? ESTADO_DE_BAJA : ESTADO_ACTIVO),
        // GRAY para "De baja" a propósito (no RED): es un estado administrativo reversible,
        // no un error ni una urgencia (CLAUDE.md, semántica de badges)
        renderCell: (params) => {
            const deBaja = Boolean(params.row.fechaBaja);
            const color = deBaja ? BADGE_COLORS.GRAY : BADGE_COLORS.GREEN;

            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Chip label={params.value} size="small" sx={getBadgeStyles(color)} />
                    {deBaja && (
                        <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.SECUNDARIO, color: "#6c757d" }}>
                            Desde {DateUtils.formatearFecha(params.row.fechaBaja)}
                        </Typography>
                    )}
                </Box>
            );
        },
    },
    {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 0.7,
        sortable: false,
        filterable: false,
        minWidth: 100,
        cellClassName: "actions",
        // DECISION: igual que en rangos/columns.js, las acciones reciben la fila completa (no
        // solo el id) para que la página pueda pasar el mismo handler al DataGrid y a
        // UsuariosMobileList sin envoltorios, y para que los modales de baja/restaurar/edición
        // reciban directamente los datos que necesitan (username para el texto de confirmación)
        getActions: ({ row }) => {
            const disabled = idOperacionEnCurso !== null && idOperacionEnCurso === row.id;
            const deBaja = Boolean(row.fechaBaja);

            const acciones = [
                <GridActionsCellItem
                    key="editar"
                    icon={<EditIcon />}
                    label="Editar usuario"
                    onClick={() => onEditar(row)}
                    disabled={disabled}
                    color="inherit"
                />,
            ];

            // Nunca conviven ambas acciones en la misma fila: activo -> solo "Dar de baja",
            // de baja -> solo "Restaurar". Nunca un icono de papelera (sugeriría borrado
            // físico); PersonOffIcon/RestoreIcon comunican una baja reversible
            acciones.push(
                deBaja ? (
                    <GridActionsCellItem
                        key="restaurar"
                        icon={<RestoreIcon />}
                        label="Restaurar usuario"
                        onClick={() => onRestaurar(row)}
                        disabled={disabled}
                        color="inherit"
                    />
                ) : (
                    <GridActionsCellItem
                        key="dar-baja"
                        icon={<PersonOffIcon />}
                        label="Dar de baja"
                        onClick={() => onDarBaja(row)}
                        disabled={disabled}
                        color="inherit"
                    />
                )
            );

            return acciones;
        },
    },
];
